import { useEffect } from 'react';

type BreadcrumbItem = {
  name: string;
  url: string;
};

type SEOProps = {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'product';
  keywords?: string;
  noIndex?: boolean;
  jsonLd?: object | object[];
  locale?: string;
  alternateLocales?: string[];
  breadcrumbs?: BreadcrumbItem[];
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
};

const DEFAULTS = {
  title: 'Studio RocheColombe — Artiste peintre contemporain',
  description:
    "Studio RocheColombe — artiste peintre contemporain à Montélimar. Art abstrait et art cinétique, acrylique, résine, galerie d'art et expositions.",
  image: 'https://static.lumi.new/44/4495c6feb2104e9b3b6d9f89ff4c7729.webp',
  siteName: 'Studio RocheColombe',
  baseUrl: 'https://www.studiorochecolombe.com',
  twitterHandle: '@studiorochecolombe',
  locale: 'en_US',
};

const absoluteUrl = (url?: string): string => {
  if (!url) return DEFAULTS.baseUrl + '/';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return DEFAULTS.baseUrl + url;
  return DEFAULTS.baseUrl + '/' + url;
};

const canonicalUrl = (url?: string): string => {
  const raw = url
    ? absoluteUrl(url)
    : (typeof window !== 'undefined' ? window.location.href : DEFAULTS.baseUrl + '/');
  return raw.split('#')[0].split('?')[0];
};

const buildPageTitle = (title?: string): string => {
  if (!title) return DEFAULTS.title;
  if (title.includes(DEFAULTS.siteName)) return title;
  return `${title} | ${DEFAULTS.siteName}`;
};

const upsertMeta = (
  selector: string,
  attr: 'name' | 'property',
  key: string,
  value: string,
) => {
  if (!value) return;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const upsertLink = (rel: string, href: string, extraAttrs?: Record<string, string>) => {
  if (!href) return;
  const selectorKey = extraAttrs?.hreflang
    ? `link[rel="${rel}"][hreflang="${extraAttrs.hreflang}"]`
    : `link[rel="${rel}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selectorKey);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  if (extraAttrs) {
    Object.entries(extraAttrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
};

const removeJsonLd = () => {
  document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
};

const addJsonLd = (data: object | object[]) => {
  removeJsonLd();
  const items = Array.isArray(data) ? data : [data];
  items.forEach((obj) => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-jsonld', 'true');
    script.textContent = JSON.stringify(obj);
    document.head.appendChild(script);
  });
};

const buildBreadcrumbJsonLd = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  keywords,
  noIndex = false,
  jsonLd,
  locale,
  alternateLocales,
  breadcrumbs,
  publishedTime,
  modifiedTime,
  author,
}: SEOProps) => {
  useEffect(() => {
    const finalTitle = buildPageTitle(title);
    const finalDesc = description || DEFAULTS.description;
    const finalImage = image || DEFAULTS.image;
    const finalUrl = canonicalUrl(url);
    const finalLocale = locale || DEFAULTS.locale;
    const robotsValue = noIndex
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

    document.title = finalTitle;

    upsertMeta('meta[name="title"]', 'name', 'title', finalTitle);
    upsertMeta('meta[name="description"]', 'name', 'description', finalDesc);
    if (keywords) upsertMeta('meta[name="keywords"]', 'name', 'keywords', keywords);
    upsertMeta('meta[name="robots"]', 'name', 'robots', robotsValue);
    upsertMeta('meta[name="googlebot"]', 'name', 'googlebot', noIndex ? 'noindex, nofollow' : 'index, follow');
    if (author) upsertMeta('meta[name="author"]', 'name', 'author', author);

    upsertMeta('meta[property="og:title"]', 'property', 'og:title', finalTitle);
    upsertMeta('meta[property="og:description"]', 'property', 'og:description', finalDesc);
    upsertMeta('meta[property="og:image"]', 'property', 'og:image', finalImage);
    upsertMeta('meta[property="og:image:alt"]', 'property', 'og:image:alt', finalTitle);
    upsertMeta('meta[property="og:image:width"]', 'property', 'og:image:width', '1200');
    upsertMeta('meta[property="og:image:height"]', 'property', 'og:image:height', '630');
    upsertMeta('meta[property="og:url"]', 'property', 'og:url', finalUrl);
    upsertMeta('meta[property="og:type"]', 'property', 'og:type', type);
    upsertMeta('meta[property="og:site_name"]', 'property', 'og:site_name', DEFAULTS.siteName);
    upsertMeta('meta[property="og:locale"]', 'property', 'og:locale', finalLocale);

    document.head
      .querySelectorAll('meta[property="og:locale:alternate"]')
      .forEach((n) => n.remove());
    const locales = alternateLocales?.length ? alternateLocales : ['fr_FR'];
    locales.forEach((alt) => {
      if (alt === finalLocale) return;
      const el = document.createElement('meta');
      el.setAttribute('property', 'og:locale:alternate');
      el.setAttribute('content', alt);
      document.head.appendChild(el);
    });

    if (type === 'article') {
      if (publishedTime)
        upsertMeta(
          'meta[property="article:published_time"]',
          'property',
          'article:published_time',
          publishedTime,
        );
      if (modifiedTime)
        upsertMeta(
          'meta[property="article:modified_time"]',
          'property',
          'article:modified_time',
          modifiedTime,
        );
      if (author)
        upsertMeta('meta[property="article:author"]', 'property', 'article:author', author);
    }

    upsertMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary_large_image');
    upsertMeta('meta[name="twitter:title"]', 'name', 'twitter:title', finalTitle);
    upsertMeta('meta[name="twitter:description"]', 'name', 'twitter:description', finalDesc);
    upsertMeta('meta[name="twitter:image"]', 'name', 'twitter:image', finalImage);
    upsertMeta('meta[name="twitter:image:alt"]', 'name', 'twitter:image:alt', finalTitle);
    upsertMeta('meta[name="twitter:url"]', 'name', 'twitter:url', finalUrl);
    upsertMeta('meta[name="twitter:site"]', 'name', 'twitter:site', DEFAULTS.twitterHandle);
    upsertMeta('meta[name="twitter:creator"]', 'name', 'twitter:creator', DEFAULTS.twitterHandle);

    upsertLink('canonical', finalUrl);
    upsertLink('alternate', finalUrl, { hreflang: 'en' });
    upsertLink('alternate', finalUrl, { hreflang: 'fr' });
    upsertLink('alternate', finalUrl, { hreflang: 'x-default' });

    const payload: object[] = [];
    if (jsonLd) {
      if (Array.isArray(jsonLd)) payload.push(...jsonLd);
      else payload.push(jsonLd);
    }
    if (breadcrumbs && breadcrumbs.length > 0) {
      payload.push(buildBreadcrumbJsonLd(breadcrumbs));
    }
    if (payload.length > 0) {
      addJsonLd(payload);
    } else {
      removeJsonLd();
    }

    return () => {
      removeJsonLd();
    };
  }, [
    title,
    description,
    image,
    url,
    type,
    keywords,
    noIndex,
    jsonLd,
    locale,
    alternateLocales,
    breadcrumbs,
    publishedTime,
    modifiedTime,
    author,
  ]);

  return null;
};

export default SEO;
